import { z } from "zod";
import { pageDetailsSchema } from "./pageContext/details";

const runNowArrivalSchema = z.object({ runNow: z.literal(true) });

/** Router state asking a routine's page to run the routine it opens. */
type RunNowArrival = z.infer<typeof runNowArrivalSchema>;

export const RUN_NOW_ARRIVAL: RunNowArrival = { runNow: true };

/** Whether the page was opened to run its automation at once. */
export const isRunNowArrival = (state: unknown): state is RunNowArrival =>
  runNowArrivalSchema.safeParse(state).success;

const runLinkArrivalSchema = z.object({ fromRunLink: z.literal(true) });

/** Router state saying the routines were reached from an old link to one run. */
type RunLinkArrival = z.infer<typeof runLinkArrivalSchema>;

export const RUN_LINK_ARRIVAL: RunLinkArrival = { fromRunLink: true };

/** Whether the routines were opened from an old link to one run. */
export const isRunLinkArrival = (state: unknown): state is RunLinkArrival =>
  runLinkArrivalSchema.safeParse(state).success;

/** Router state opening a new private chat, with an optional first send. */
const newChatDraftSchema = z.object({
  composerDraft: z.string(),
  sendOnArrival: z.boolean().optional(),
  demonstrate: z.boolean().optional(),
  /** The company whose chat takes the first send; it waits for the session to be there. */
  companyId: z.string().optional(),
  /**
   * The page the chat was opened from. A caller that names the page on
   * screen may carry its details too: the registry is gone by the time the
   * chat mounts, so a send would otherwise arrive knowing the path and
   * nothing about what was on it. A caller naming somewhere *else* — a
   * checklist step linking to another page, or another company — must leave
   * `details` off, because they would describe a page the message is not
   * about. The send bounds whatever arrives here regardless.
   */
  pageContext: z
    .object({
      path: z.string(),
      title: z.string().optional(),
      /**
       * `.catch` rather than a plain optional: details that do not hold up
       * are dropped on their own, and the draft still opens the chat with
       * the person's message. Validating them strictly here would throw the
       * message away with them, which is the one outcome evidence is never
       * worth. The send bounds them again anyway.
       */
      details: pageDetailsSchema.optional().catch(undefined),
    })
    .optional(),
});

export type NewChatDraft = z.infer<typeof newChatDraftSchema>;

export const newChatDraft = (
  composerDraft: string,
  options: Omit<NewChatDraft, "composerDraft"> = {},
): NewChatDraft => ({ composerDraft, ...options });

/** Whether the new chat was opened with a draft for its composer. */
export const isNewChatDraft = (state: unknown): state is NewChatDraft =>
  newChatDraftSchema.safeParse(state).success;
